import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Flower, MapPin, Users2 } from "lucide-react";

// "More people we're remembering from {scope}" strip. Rendered at the
// bottom of every memorial detail page for discovery. Silent when no
// related memorials exist.
//
// The backend cascades city → state → country → source, so the label
// adapts to whatever scope actually yielded matches.
export default function RelatedMemorialsStrip({ memorial }) {
  const [state, setState] = useState({ loading: true, items: [], scope: "none" });

  useEffect(() => {
    if (!memorial?.slug && !memorial?.id) return;
    let cancelled = false;
    setState({ loading: true, items: [], scope: "none" });
    http.get(`/memorials/${memorial.slug || memorial.id}/related`, { params: { limit: 6 } })
      .then((r) => { if (!cancelled) setState({ loading: false, items: r.data.items || [], scope: r.data.scope || "none" }); })
      .catch(() => { if (!cancelled) setState({ loading: false, items: [], scope: "none" }); });
    return () => { cancelled = true; };
  }, [memorial?.slug, memorial?.id]);

  if (state.loading || state.items.length === 0) return null;

  const loc = memorial.location || {};
  let scopeLabel = "we're also remembering";
  switch (state.scope) {
    case "city":    scopeLabel = `from ${loc.city}`; break;
    case "state":   scopeLabel = `from ${loc.state}`; break;
    case "country": scopeLabel = `from ${loc.country}`; break;
    case "source":  scopeLabel = `also imported from ${memorial.source_attribution || memorial.source}`; break;
    default:        scopeLabel = "we're also remembering";
  }

  return (
    <section className="mt-14 mb-10" data-testid="related-memorials-strip">
      <div className="flex items-baseline gap-3 mb-6">
        <Users2 className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl md:text-3xl">More people {scopeLabel}</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {state.items.slice(0, 6).map((m) => (
          <Link
            key={m.id}
            to={`/celebrate/${m.slug || m.id}`}
            data-testid={`related-card-${m.id}`}
            className="wofy-card flex gap-3 items-start hover:shadow-md transition-shadow"
          >
            {m.portrait ? (
              <img src={m.portrait} alt={m.name_latin || m.full_name || "Portrait"} loading="lazy" className="w-16 h-16 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                <Flower className="w-6 h-6 opacity-30" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-base leading-tight truncate">
                {m.name_latin || m.preferred_name || m.full_name}
              </div>
              <div className="mt-0.5 text-xs flex items-center gap-1 flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
                {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
                {m.age_at_death && m.location?.city ? <span>·</span> : null}
                {m.location?.city ? <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{m.location.city}</span> : null}
              </div>
              {m.english_snippet && (
                <p className="mt-1.5 text-xs leading-snug line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                  {m.english_snippet}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
